/**
 * Insights — Layout route for the AI platform analytics module.
 *
 * Mission: turn months of audit events into the 5 things an admin needs to do today.
 * NOT a generic BI tool. Opinionated dashboard for tenant operators.
 *
 * The 5 tabs answer 5 distinct operator questions:
 *   - Overview          "Is the platform healthy and being used?"
 *   - Adoption          "Who's using it, where are gaps, is it growing?"
 *   - Quality & Safety  "Are answers good? What got blocked?"
 *   - Cost & Capacity   "What's it costing? Is capacity sufficient?"
 *   - Agents & Knowledge "Which agents and docs are earning their keep?"
 *
 * Plus a persistent Action Items drawer that travels with the admin across tabs.
 */

import { createFileRoute, Outlet, useLocation } from '@tanstack/react-router';
import { ContentHeader } from '@/components/content-header';
import {
  LayoutDashboard, Users, ShieldCheck, DollarSign, Bot,
  TrendingUp, ShieldAlert,
} from 'lucide-react';

export const Route = createFileRoute('/insights')({
  component: InsightsLayout,
});

// R3 P1 fix 2026-05-13 — added Value + Risk tabs so direct URLs no
// longer 404. The canonical 5 tabs from INSIGHTS-UI-SPEC remain first
// in order (Overview / Adoption / Quality & Safety / Cost & Capacity /
// Agents & Knowledge); Value + Risk surface the PR #430 backend
// endpoints (`/insights-module/value` + `/insights-module/risk`) that
// previously had no UI consumer. The Overview ROI card + Quality
// Safety sub-tab still surface the same data in their spec-canonical
// positions.
const INSIGHTS_TABS = [
  { label: 'Overview',          path: '/insights',                  icon: LayoutDashboard },
  { label: 'Adoption',          path: '/insights/adoption',         icon: Users },
  { label: 'Quality & Safety',  path: '/insights/quality',          icon: ShieldCheck },
  { label: 'Cost & Capacity',   path: '/insights/cost',             icon: DollarSign },
  { label: 'Agents & Knowledge', path: '/insights/agents-knowledge', icon: Bot },
  { label: 'Value',             path: '/insights/value',            icon: TrendingUp },
  { label: 'Risk',              path: '/insights/risk',             icon: ShieldAlert },
];

function InsightsLayout() {
  const location = useLocation();
  // Show tabs on all top-level insights routes; hide on potential detail routes
  const showTabs = INSIGHTS_TABS.some(t =>
    t.path === location.pathname || location.pathname === t.path + '/',
  );

  return (
    <div className="flex flex-col h-full">
      {showTabs && <ContentHeader title="Insights" tabs={INSIGHTS_TABS} />}
      <div className="flex-1 overflow-auto">
        <Outlet />
      </div>
    </div>
  );
}
